import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import '../styles/MinyanShare.css';

const APP_STORE_URL  = 'https://apps.apple.com/us/app/yesod/id6748472269';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=us.yesod.app';

const PRAYER_MAP = {
  shacharit: { label: 'Shacharit', color: '#f59e0b', bg: '#fffbeb', textColor: '#92400e' },
  mincha:    { label: 'Mincha',    color: '#0ea5e9', bg: '#f0f9ff', textColor: '#075985' },
  maariv:    { label: 'Maariv',    color: '#6366f1', bg: '#eef2ff', textColor: '#3730a3' },
};

const parseTo24h = (t) => {
  if (!t) return '';
  const s = t.trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(':');
    return `${String(parseInt(h, 10)).padStart(2, '0')}:${m}`;
  }
  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const p = m12[3].toLowerCase();
    if (p === 'pm' && h !== 12) h += 12;
    if (p === 'am' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${m12[2]}`;
  }
  return s;
};

const formatDisplay = (t) => {
  const s = parseTo24h(t);
  if (!s || !s.includes(':')) return t || '';
  const [h24, m] = s.split(':');
  const h = parseInt(h24, 10);
  const period = h >= 12 ? 'PM' : 'AM';
  const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${dh}:${m} ${period}`;
};

// ─── Features list ────────────────────────────────────────────────────────────
const PERKS = [
  'Save favorite minyan times',
  'Push reminder before davening',
  'Alerts when times change',
];

export default function MinyanSharePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [shared, setShared]   = useState(false);

  const time       = searchParams.get('t') || '';
  const prayerType = (searchParams.get('p') || '').toLowerCase();
  const groupName  = searchParams.get('n') || 'Synagogue';
  const city       = searchParams.get('c') || '';
  const state      = searchParams.get('s') || '';
  const address    = searchParams.get('a') || '';
  const dayStr     = searchParams.get('d') || '';

  const prayer = PRAYER_MAP[prayerType] || {
    label: 'Prayer Time', color: '#6366f1', bg: '#eef2ff', textColor: '#3730a3',
  };

  const today   = new Date();
  const dateStr = dayStr || `${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][today.getDay()]}, ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][today.getMonth()]} ${today.getDate()}`;

  const displayTime = formatDisplay(time) || '—';

  const addressLine = [address, city && state ? `${city}, ${state}` : (city || state)]
    .filter(Boolean).join(' · ');

  const openDirections = () => {
    const q = address
      ? encodeURIComponent(`${address}, ${city} ${state}`.trim())
      : encodeURIComponent(`${groupName} ${city} ${state}`.trim());
    window.open(`https://maps.google.com/?q=${q}`, '_blank', 'noopener');
  };

  const handleShare = () => {
    const shareData = {
      title: `${prayer.label} at ${displayTime}`,
      text:  `${prayer.label} at ${displayTime} – ${groupName}${city ? `, ${city}` : ''}`,
      url:   window.location.href,
    };
    if (navigator.share) {
      navigator.share(shareData).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href).then(() => {
        setShared(true);
        setTimeout(() => setShared(false), 2200);
      }).catch(() => {});
    }
  };

  return (
    <div className="ms">

      {/* ── Minimal nav ─────────────────────────────────────── */}
      <header className="ms-nav">
        <button className="ms-nav__back" onClick={() => navigate('/minyan')}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
          Find a Minyan
        </button>
        <span className="ms-nav__brand">Yesod</span>
      </header>

      <main className="ms-main">

        {/* ── The answer card ───────────────────────────────── */}
        <div className="ms-card">
          <div className="ms-card__accent" style={{ background: prayer.color }} />

          {/* Prayer type + date */}
          <div className="ms-card__top">
            <span className="ms-card__badge"
              style={{ background: prayer.bg, color: prayer.textColor }}>
              <span className="ms-card__badge-dot" style={{ background: prayer.color }} />
              {prayer.label}
            </span>
            <span className="ms-card__date">{dateStr}</span>
          </div>

          {/* TIME — the hero answer */}
          <p className="ms-card__time">{displayTime}</p>

          <div className="ms-card__divider" />

          {/* Shul info */}
          <p className="ms-card__group">{groupName}</p>
          {addressLine && <p className="ms-card__address">{addressLine}</p>}

          {/* Actions */}
          <div className="ms-card__actions">
            <button className="ms-card__dir-btn" onClick={openDirections}>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M3 11l19-9-9 19-2-8-8-2z"/>
              </svg>
              Directions
            </button>
            <button className="ms-card__share-btn" onClick={handleShare}>
              {shared ? (
                <>
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                  </svg>
                  Share
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Retention block ───────────────────────────────── */}
        <div className="ms-retention">
          <p className="ms-retention__title">Save this & get reminders</p>
          <p className="ms-retention__sub">Free in the Yesod app</p>
          <ul className="ms-retention__perks">
            {PERKS.map(p => <li key={p}>{p}</li>)}
          </ul>
          <div className="ms-retention__stores">
            <a className="ms-store-btn ms-store-btn--ios"
              href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11"/>
              </svg>
              App Store
            </a>
            <a className="ms-store-btn ms-store-btn--android"
              href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                <path d="M3.18 23.77c.26.15.55.23.84.23.28 0 .56-.07.82-.21l10.96-6.35-2.62-2.62-10 9.95zm-1.18-21c-.01.1-.01.2-.01.3V20.9c0 .11 0 .21.01.32l10.32-10.32L2 2.77zm19.53 8.93L19.73 10l-2.33-1.35L14.83 12l2.57 2.57 2.39-1.38c.68-.41.97-1.08.74-1.49zM4.01.44l10 9.95 2.62-2.62L5.67.42c-.52-.3-1.17-.25-1.66.02z"/>
              </svg>
              Google Play
            </a>
          </div>
        </div>

        <p className="ms-footer">Sent via <strong>Yesod</strong></p>

      </main>
    </div>
  );
}
